import type { SnapshotSearchParams, PlayerStats } from '../api/intel';

/**
 * SF-171: extracted from `TargetFinderTab.tsx`'s inline `pctToAbsolute` + percent-range
 * param-building block (was lines ~108-163) so both desktop's `TargetFinderTab.tsx` and the new
 * mobile filter sheet (`MobileTargetFilterSheet.tsx`) build search params from one shared source
 * of truth instead of two independently-hand-derived copies (the SF-140 "hand-derived duplicate
 * logic diverges from the original" class of bug). Behavior-preserving extraction — see
 * `targetSearchParams.test.ts` and `TargetFinderTab.test.tsx`'s corrected param-shape assertion.
 */

export type SortKey = 'nw' | 'land' | 'nwpa' | 'race' | 'rank' | 'kdNw' | 'kdLand';

/** Committed filter state for the 7 dimensions Target Finder supports — Province (race, rank,
 *  networth, NW/acre, land) + Kingdom (kingdom networth, kingdom land). Min/max values are kept
 *  as strings (matching the existing `<input type="number">` controlled-value convention) —
 *  conversion to absolute numbers happens only inside `buildTargetSearchParams`. */
export interface TargetFilterState {
  raceEnabled: boolean;
  raceSelected: string[];
  rankEnabled: boolean;
  rankSelected: string[];
  nwEnabled: boolean;
  nwMin: string;
  nwMax: string;
  landEnabled: boolean;
  landMin: string;
  landMax: string;
  nwpaEnabled: boolean;
  nwpaMin: string;
  nwpaMax: string;
  kdNwEnabled: boolean;
  kdNwMin: string;
  kdNwMax: string;
  kdLandEnabled: boolean;
  kdLandMin: string;
  kdLandMax: string;
}

/** Percentage-of-reference-value string ("80") -> absolute value, rounded. `undefined` on a
 *  malformed (non-numeric) input, matching `TargetFinderTab.tsx`'s original NaN-guard. */
export function pctToAbsolute(pctStr: string, refValue: number): number | undefined {
  const pct = parseInt(pctStr, 10);
  if (isNaN(pct)) return undefined;
  return Math.round((refValue * pct) / 100);
}

/**
 * Builds the `SnapshotSearchParams` payload for `searchSnapshots()` from committed filter/sort/
 * page state. `size` is a parameter (not hardcoded) so desktop (50) and mobile (20) each supply
 * their own. Percent-range dimensions (land/NW-per-acre/kingdom-networth/kingdom-land) are
 * skipped entirely when `playerStats` is `null` — the first-search-ever case, before the response
 * has returned the player's own stats to convert percentages against. Networth is the exception:
 * it defaults to enabled (own networth ±20%), so `ownNetworthFallback` (the player's own province
 * networth, already available client-side via `useProvince()` before any search has run) seeds
 * that first search too — falling back to `playerStats.networth` once a response has come back.
 */
export function buildTargetSearchParams(
  filters: TargetFilterState,
  sortBy: SortKey,
  sortDir: 'asc' | 'desc',
  page: number,
  size: number,
  playerStats: PlayerStats | null,
  ownNetworthFallback?: number | null
): SnapshotSearchParams {
  const params: SnapshotSearchParams = { sortBy, sortDir, page, size };

  if (filters.raceEnabled && filters.raceSelected.length > 0) params.raceIn = filters.raceSelected;
  if (filters.rankEnabled && filters.rankSelected.length > 0) params.rankIn = filters.rankSelected;

  const networthRef = playerStats?.networth ?? ownNetworthFallback ?? undefined;
  if (filters.nwEnabled && networthRef !== undefined) {
    const min = pctToAbsolute(filters.nwMin, networthRef);
    const max = pctToAbsolute(filters.nwMax, networthRef);
    if (min !== undefined) params.nwMin = min;
    if (max !== undefined) params.nwMax = max;
  }

  if (playerStats) {
    if (filters.landEnabled) {
      const min = pctToAbsolute(filters.landMin, playerStats.land);
      const max = pctToAbsolute(filters.landMax, playerStats.land);
      if (min !== undefined) params.landMin = min;
      if (max !== undefined) params.landMax = max;
    }
    if (filters.nwpaEnabled) {
      const min = pctToAbsolute(filters.nwpaMin, playerStats.nwpa);
      const max = pctToAbsolute(filters.nwpaMax, playerStats.nwpa);
      if (min !== undefined) params.nwpaMin = min;
      if (max !== undefined) params.nwpaMax = max;
    }
    if (filters.kdNwEnabled) {
      const min = pctToAbsolute(filters.kdNwMin, playerStats.kingdomNW);
      const max = pctToAbsolute(filters.kdNwMax, playerStats.kingdomNW);
      if (min !== undefined) params.kdNwMin = min;
      if (max !== undefined) params.kdNwMax = max;
    }
    if (filters.kdLandEnabled) {
      const min = pctToAbsolute(filters.kdLandMin, playerStats.kingdomLand);
      const max = pctToAbsolute(filters.kdLandMax, playerStats.kingdomLand);
      if (min !== undefined) params.kdLandMin = min;
      if (max !== undefined) params.kdLandMax = max;
    }
  }
  // If no playerStats yet (first search), skip range filters — the response will return playerStats.

  return params;
}
